import React, { useState, useEffect } from 'react';
import { Nivel, Instructor } from '../../types/index';
import { NIVEL_COLOR_PALETTE, pickNextLevelColor } from '../../utils/levelColors';

// Define a union type for the entity being edited
type EntityType = 'nivel' | 'instructor';

interface Entity {
    _id?: string;
    nombre: string;
    color?: string;  // Solo para niveles
    email?: string | null;  // Solo para instructores (P6 fase 2)
}

interface NivelInstructorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (entity: { nombre: string; color?: string; email?: string }) => void;
    entity?: Entity;
    entityType: EntityType;
    /** Colores ya usados por otros niveles, para no repetir en uno nuevo. */
    usedColors?: (string | undefined)[];
}

const NivelInstructorModal: React.FC<NivelInstructorModalProps> = ({
    isOpen,
    onClose,
    onSave,
    entity,
    entityType,
    usedColors = [],
}) => {
    // Un nivel NUEVO nace con el primer color libre de la paleta (antes todos
    // nacían con el mismo verde y el calendario quedaba monocromo).
    const defaultColor = pickNextLevelColor(usedColors);
    const [nombre, setNombre] = useState(entity?.nombre || '');
    const [color, setColor] = useState(entity?.color || defaultColor);
    const [email, setEmail] = useState(entity?.email || '');

    useEffect(() => {
        if (entity) {
            setNombre(entity.nombre);
            setColor(entity.color || defaultColor);
            setEmail(entity.email || '');
        } else {
            setNombre('');
            setColor(defaultColor);
            setEmail('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity, isOpen]);

    const handleSave = () => {
        if (entityType === 'nivel') {
            onSave({ nombre, color });
        } else {
            onSave({ nombre, email: email.trim() });
        }
        onClose();
        setNombre('');
        setEmail('');
    };

    if (!isOpen) return null;

    const title = entity 
        ? `Editar ${entityType === 'nivel' ? 'Nivel' : 'Instructor'}`
        : `Crear ${entityType === 'nivel' ? 'Nivel' : 'Instructor'}`;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">{title}</h2>
                
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {`Nombre del ${entityType === 'nivel' ? 'Nivel' : 'Instructor'}`}
                    </label>
                    <input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        className="mt-1 p-2 border border-gray-300 rounded w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={`Nombre del ${entityType === 'nivel' ? 'nivel' : 'instructor'}`}
                    />
                </div>

                {/* P6 fase 2 — correo opcional. Sin correo, un instructor es lo
                    que ha sido siempre: un nombre en el horario. Con correo se le
                    invita y podrá entrar a pasar lista en SUS clases. Nunca se le
                    pone una contraseña: va por invitación. */}
                {entityType === 'instructor' && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Correo <span className="font-normal text-gray-400">(opcional)</span>
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1 p-2 border border-gray-300 rounded w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="profesor@ejemplo.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {entity?.email
                                ? 'Si lo cambias, se enviará una invitación nueva a esa dirección.'
                                : 'Si lo rellenas, le llegará una invitación para entrar y pasar lista en sus clases. Si lo dejas vacío, solo aparecerá en el horario.'}
                        </p>
                    </div>
                )}

                {/* Selector de color - solo para niveles */}
                {entityType === 'nivel' && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Color del Nivel
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                className="h-10 w-20 cursor-pointer border border-gray-300 rounded"
                            />
                            <input
                                type="text"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                pattern="^#[0-9A-Fa-f]{6}$"
                                className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                placeholder="#40a136"
                            />
                            <div
                                className="w-10 h-10 rounded border-2 border-gray-300 shadow-sm flex items-center justify-center"
                                style={{ backgroundColor: color }}
                                title="Preview del color"
                            >
                                {/* El texto del calendario va en blanco: así se ve el contraste real */}
                                <span className="text-white text-[10px] font-bold">Aa</span>
                            </div>
                        </div>

                        {/* Paleta rápida: tonos legibles con texto blanco y distinguibles entre sí */}
                        <div className="flex flex-wrap gap-2 mt-3">
                            {NIVEL_COLOR_PALETTE.map((c) => {
                                const enUso = usedColors.some(
                                    (u) => u?.toLowerCase() === c.toLowerCase() && u?.toLowerCase() !== entity?.color?.toLowerCase(),
                                );
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setColor(c)}
                                        title={enUso ? `${c} — ya usado por otro nivel` : c}
                                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                                            color.toLowerCase() === c.toLowerCase()
                                                ? 'border-gray-800 ring-2 ring-offset-1 ring-gray-400'
                                                : 'border-white shadow'
                                        } ${enUso ? 'opacity-40' : ''}`}
                                        style={{ backgroundColor: c }}
                                    />
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Este color se aplicará a todos los eventos de este nivel en el calendario
                        </p>
                    </div>
                )}

                <div className="flex justify-end space-x-2 mt-6">
                    <button 
                        onClick={onClose} 
                        className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded transition-colors"
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={handleSave} 
                        className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded transition-colors"
                        disabled={!nombre.trim()}
                    >
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NivelInstructorModal;